import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { isAIDebriefActivity } from '../../../../../pure-js/libs/isAIDebriefActivity'
import { activityUsesSprintRoom } from '../../../../../pure-js/libs/activityUsesDedicatedRoom'
import type { ActivityWithSession, ActivityWithSessions } from '../../../types/GeneralTypes'
import type { useTranslation } from '../../../shared/hooks/useIntractionTranslate'

/** The `t` of `useTranslation`. */
export type TranslateFunction = ReturnType<typeof useTranslation>['t']

/** A plain key-and-fallback translator, which `t` is used as. */
export type TranslationFunction = (key: string, defaultValue?: string) => string

const ACTIVITY_TYPE_OPTION_KEYS: Partial<Record<ActivityType, string>> = {
  [ActivityType.INTRODUCTION]: 'introduction',
  [ActivityType.FIRST_GAME]: 'first_game',
  [ActivityType.SHORT_GAME]: 'short_game',
  [ActivityType.LONG_GAME]: 'long_game',
  [ActivityType.SHORT_REFLECTION]: 'short_reflection',
  [ActivityType.LONG_REFLECTION]: 'long_reflection'
}

const ACTIVITY_TYPE_OPTION_FALLBACKS: Partial<Record<ActivityType, string>> = {
  [ActivityType.INTRODUCTION]: 'Introduction',
  [ActivityType.FIRST_GAME]: '1st game session',
  [ActivityType.SHORT_GAME]: 'Short game session',
  [ActivityType.LONG_GAME]: 'Long game session',
  [ActivityType.SHORT_REFLECTION]: 'AI coached team debrief',
  [ActivityType.LONG_REFLECTION]: 'Group Reflection'
}

/** The translated name of an activity type: Introduction, 1st game session, Group Reflection… */
export const getActivityTypeLabel = (type: ActivityType, t: TranslationFunction): string =>
  t(
    `facilitator.sprintDetails.activityTypeOptions.${ACTIVITY_TYPE_OPTION_KEYS[type] ?? type}`,
    ACTIVITY_TYPE_OPTION_FALLBACKS[type]
  )

/** What a call of the whole sprint is called on its page and on the Control Tower: Introduction, Group Reflection. */
export const getSprintCallTitle = (activityType: ActivityType | undefined, t: TranslateFunction): string => {
  const isSprintCall = !!activityType && activityUsesSprintRoom(activityType)
  return isSprintCall ? getActivityTypeLabel(activityType, t as TranslationFunction) : ''
}

/**
 * Check if an activity is currently live (within its start and end time window)
 * @param activity - Activity to check
 * @param userTeamIds - Optional user team IDs to check if the team has already completed the activity
 * @returns true if activity is live, false otherwise
 */
export const isActivityLive = (activity: ActivityWithSessions, userTeamIds?: string[]): boolean => {
  if (activity.endedAt) return false
  if (!activity.startDate || !activity.endDate) return false

  // If the user's team has already completed this activity, it's not live for them
  if (userTeamIds?.length && activity.completedActivityTeamIds?.some((id) => userTeamIds.includes(id))) {
    return false
  }

  const now = Date.now()
  const start = new Date(activity.startDate).getTime()
  const end = new Date(activity.endDate).getTime()

  return now >= start && now <= end
}

/**
 * Check if any activity in the list is currently live
 * @param activities - List of activities
 * @param userTeamIds - Optional user team IDs to exclude completed activities
 * @returns true if at least one activity is live
 */
export const hasAnyLiveActivity = (activities: ActivityWithSessions[], userTeamIds?: string[]): boolean => {
  if (!activities?.length) return false
  return activities.some((a) => isActivityLive(a, userTeamIds))
}

/** The team may enter its AI debrief now: once their game is over it opens, often before its scheduled start. */
export const isAIDebriefOpenForTeam = (currentActivity?: ActivityWithSession | null): boolean =>
  isAIDebriefActivity(currentActivity?.type) && Boolean(currentActivity?.aiCoach?.canStart)
